export interface NormalizedOrderListItem {
    id: string;
    order_number: string;
    order_no?: string;
    total_price: number;
    total?: number;
    is_paid: boolean;
    created_at: string;
    opened_at?: string;
    status?: string;
    order_type?: string;
    zone_name_snapshot?: string;
    table_number_snapshot?: string | number;
    raw?: any;
}

const toStringValue = (value: any): string | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : null;
    }

    if (typeof value === "number" && !Number.isNaN(value)) {
        return String(value);
    }

    return null;
};

const toNumberValue = (value: any): number | null => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

const candidateReducer = <T>(values: any[], parser: (value: any) => T | null, fallback?: T): T => {
    for (const value of values) {
        const parsed = parser(value);
        if (parsed !== null && parsed !== undefined) {
            return parsed;
        }
    }

    return fallback as T;
};

const fallbackOrderId = () => `order-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getOrderId = (order: any): string => {
    const candidates = [order?.id, order?.order_id, order?.uuid, order?.code, order?.number];
    return candidateReducer<string>(candidates, toStringValue, fallbackOrderId());
};

export const getOrderNumber = (order: any): string => {
    const candidates = [
        order?.order_number,
        order?.orderNumber,
        order?.displayCode,
        order?.display_code,
        order?.code,
        order?.number,
        order?.name,
    ];

    const parsed = candidateReducer<string | null>(candidates, toStringValue, null);
    return parsed ?? `#${getOrderId(order)}`;
};

export const getOrderTotalPrice = (order: any): number => {
    const candidates = [
        order?.total,
        order?.total_price,
        order?.totalPrice,
        order?.amount,
        order?.amount_due,
        order?.grand_total,
        order?.price,
    ];

    return candidateReducer<number>(candidates, toNumberValue, 0) ?? 0;
};

export const getOrderTotal = (order: any): number => {
    const candidates = [
        order?.total,
        order?.total_price,
        order?.totalPrice,
        order?.amount,
        order?.amount_due,
        order?.grand_total,
        order?.price,
    ];

    return candidateReducer<number>(candidates, toNumberValue, 0) ?? 0;
};

export const getOrderIsPaid = (order: any): boolean => {
    const candidates = [
        order?.is_paid,
        order?.isPaid,
        order?.paid,
        order?.payment_status,
        order?.paymentStatus,
        order?.statusPayment,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "boolean") {
            return candidate;
        }

        if (typeof candidate === "number") {
            return candidate !== 0;
        }

        if (typeof candidate === "string") {
            const normalized = candidate.trim().toLowerCase();
            if (["1", "paid", "done", "completed", "đã thanh toán"].includes(normalized)) {
                return true;
            }
            if (["0", "unpaid", "pending", "chưa thanh toán"].includes(normalized)) {
                return false;
            }
        }
    }

    return false;
};

export const getOrderCreatedAt = (order: any): string => {
    const candidates = [order?.created_at, order?.createdAt, order?.updated_at, order?.updatedAt];
    return candidateReducer<string>(candidates, toStringValue, new Date().toISOString());
};

export const getOrderOpenedAt = (order: any): string => {
    const candidates = [order?.opened_at, order?.openedAt, order?.created_at, order?.createdAt];
    return candidateReducer<string>(candidates, toStringValue, getOrderCreatedAt(order));
};

export const getOrderNo = (order: any): string | null => {
    const candidates = [order?.order_no, order?.orderNo, order?.order_number, order?.orderNumber];
    return candidateReducer<string | null>(candidates, toStringValue, null);
};

export const getOrderStatus = (order: any): string | null => {
    const candidates = [order?.status, order?.order_status, order?.state];
    return candidateReducer<string | null>(candidates, toStringValue, null);
};

export const getOrderType = (order: any): string | null => {
    const candidates = [order?.order_type, order?.orderType, order?.type];
    return candidateReducer<string | null>(candidates, toStringValue, null);
};

export const getOrderZoneSnapshot = (order: any): string | null => {
    const candidates = [
        order?.zone_name_snapshot,
        order?.zoneNameSnapshot,
        order?.zone_name,
        order?.zoneName,
    ];
    return candidateReducer<string | null>(candidates, toStringValue, null);
};

export const getOrderTableSnapshot = (order: any): string | number | null => {
    const candidates = [
        order?.table_number_snapshot,
        order?.tableNumberSnapshot,
        order?.table_number,
        order?.tableNumber,
    ];
    return candidateReducer<string | number | null>(candidates, (value) => {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === "number") {
            return value;
        }
        const parsed = toStringValue(value);
        return parsed ?? null;
    }, null);
};

const extractOrderArray = (response: any): any[] => {
    if (!response) {
        return [];
    }

    const direct = response.data ?? response;

    if (Array.isArray(direct)) {
        return direct;
    }

    if (Array.isArray(direct?.data)) {
        return direct.data;
    }

    if (Array.isArray(direct?.data?.data)) {
        return direct.data.data;
    }

    if (Array.isArray(direct?.orders)) {
        return direct.orders;
    }

    return [];
};

export const normalizeOrdersForList = (response: any): NormalizedOrderListItem[] => {
    return extractOrderArray(response).map((order) => ({
        id: getOrderId(order),
        order_number: getOrderNumber(order),
        order_no: getOrderNo(order) ?? undefined,
        total_price: getOrderTotalPrice(order),
        total: getOrderTotal(order),
        is_paid: getOrderIsPaid(order),
        created_at: getOrderCreatedAt(order),
        opened_at: getOrderOpenedAt(order),
        status: getOrderStatus(order) ?? undefined,
        order_type: getOrderType(order) ?? undefined,
        zone_name_snapshot: getOrderZoneSnapshot(order) ?? undefined,
        table_number_snapshot: getOrderTableSnapshot(order) ?? undefined,
        raw: order,
    }));
};
